import React, { useState, useEffect, useRef, useCallback } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { productAPI, bulkAPI, settingsAPI, mastersAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import ProductModal, { normalizeProduct } from '../components/ProductModal';


const Products = () => {
  const { darkMode, color } = useTheme();
  const { refreshProducts } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const openedFromUrl = useRef(searchParams.get('action') === 'create');
  const sourceRoute = useRef(searchParams.get('source') || null);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [stockStatusFilter, setStockStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(searchParams.get('action') === 'create');
  const [showStockModal, setShowStockModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [masterBrands, setMasterBrands] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);
  const [masterSubcategories, setMasterSubcategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const searchRef = useRef(null);

  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  // Clear action param after reading it
  useEffect(() => {
    if (searchParams.get('action') || searchParams.get('source')) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('action');
        next.delete('source');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setLoading(true);
    setSelectedIndex(-1);
    productAPI.list({
      page,
      limit: 12,
      search: search || undefined,
      category: categoryFilter || undefined,
      brand: brandFilter || undefined,
      subcategory: subcategoryFilter || undefined,
      supplier_id: supplierFilter || undefined,
      stock_status: stockStatusFilter === 'all' ? undefined : stockStatusFilter,
    })
      .then(r => { setProducts(r.data.products || []); setTotalPages(Math.ceil((r.data.total || 0) / 12)); setTotal(r.data.total || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [page, search, categoryFilter, brandFilter, subcategoryFilter, supplierFilter, stockStatusFilter]);

  useEffect(() => {
    mastersAPI.listBrands().then(r => setMasterBrands(r.data || [])).catch(() => {});
    mastersAPI.listCategories().then(r => setMasterCategories(r.data || [])).catch(() => {});
    mastersAPI.listSubcategories().then(r => setMasterSubcategories(r.data || [])).catch(() => {});
    productAPI.suppliersWithProducts().then(r => {
      const list = r.data?.suppliers || [];
      const orphanCount = r.data?.orphan_count || 0;
      if (orphanCount > 0) {
        list.push({ id: '__orphan__', name: `Unknown / No Supplier (${orphanCount})`, product_count: orphanCount });
      }
      setSuppliers(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    productAPI.stats({
      search: search || undefined,
      category: categoryFilter || undefined,
      brand: brandFilter || undefined,
      subcategory: subcategoryFilter || undefined,
      supplier_id: supplierFilter || undefined,
      stock_status: stockStatusFilter === 'all' ? undefined : stockStatusFilter,
    }).then(r => setStats(r.data)).catch(() => {});
  }, [search, categoryFilter, brandFilter, subcategoryFilter, supplierFilter, stockStatusFilter]);

  const handleDelete = useCallback(async id => {
    if (!window.confirm('Delete this product?')) return;
    try { await productAPI.delete(id); setPage(1); refreshProducts(true); } catch { alert('Failed to delete'); }
  }, [refreshProducts]);

  const openEditProduct = useCallback(async (productId) => {
    try {
      const response = await productAPI.get(productId);
      setSelected(normalizeProduct(response.data));
      setShowModal(true);
    } catch {
      alert('Failed to load full product details for editing');
    }
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      const tag = e.target.tagName;
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable;
      if (showModal || showStockModal || showRegisterModal) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, products.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, 0)); }
      else if (e.key === 'Escape') {
        if (selectedIndex >= 0) { setSelectedIndex(-1); }
        else if (sourceRoute.current) { const dest = sourceRoute.current; sourceRoute.current = null; openedFromUrl.current = false; navigate(dest, { replace: true }); }
      }
      else if (e.key === 'Enter' && selectedIndex >= 0 && !inInput) { e.preventDefault(); openEditProduct(products[selectedIndex].id); }
      else if (e.key === 'Delete' && selectedIndex >= 0 && !inInput) { e.preventDefault(); handleDelete(products[selectedIndex].id); }
      else if ((e.key === 'n' || e.key === 'N') && !inInput) { e.preventDefault(); setSelected(null); setShowModal(true); }
      else if ((e.key === '/' || e.key === 'f' || e.key === 'F') && !inInput) { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleDelete, navigate, openEditProduct, products, selectedIndex, showModal, showRegisterModal, showStockModal]);

  // Listen for sv-open-modal events from command palette
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === 'add-product') { setSelected(null); setShowModal(true); }
      else if (e.detail === 'stock-update' && products.length > 0) { setSelected(products[0]); setShowStockModal(true); }
    };
    window.addEventListener('sv-open-modal', handler);
    return () => window.removeEventListener('sv-open-modal', handler);
  }, [products]);

  const categories = masterCategories.map(c => c.category_name);
  const brands = masterBrands.map(b => b.brand_name);
  const subcategories = masterSubcategories
    .filter(s => !categoryFilter || s.category_name === categoryFilter)
    .map(s => s.subcategory_name);

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ ...pg, fontSize: 28, fontWeight: 800, margin: 0 }}>Products & Inventory</h1>
            <p style={{ ...muted, margin: '4px 0 0', fontSize: 14 }}>Manage your product catalogue and stock levels</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={() => navigate('/stock-audit')}
              style={{ padding: '10px 18px', background: darkMode ? '#1E293B' : '#F1F5F9', color: acc, border: `1.5px solid ${acc}`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              📋 Stock Audit
            </button>
            <button onClick={() => { setSelected(null); setShowModal(true); }}
              style={{ padding: '10px 22px', background: acc, color: '#fff', border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              ➕ Add Product
            </button>
            <button onClick={async () => { try { const r = await bulkAPI.exportProducts(); const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'products_export.xlsx'; a.click(); URL.revokeObjectURL(url); } catch { alert('Export failed'); } }}
              style={{ padding: '10px 16px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              📤 Export
            </button>
            <button onClick={async () => {
              try {
                const r = await mastersAPI.syncFromProducts();
                alert(`Masters synced. Added ${r.data.brands_added} brands, ${r.data.categories_added} categories, ${r.data.subcategories_added} subcategories.`);
                const [brandRes, categoryRes, subcategoryRes] = await Promise.all([
                  mastersAPI.listBrands(),
                  mastersAPI.listCategories(),
                  mastersAPI.listSubcategories(),
                ]);
                setMasterBrands(brandRes.data || []);
                setMasterCategories(categoryRes.data || []);
                setMasterSubcategories(subcategoryRes.data || []);
              } catch (err) {
                alert('Master sync failed: ' + (err.response?.data?.detail || err.message));
              }
            }}
              style={{ padding: '10px 16px', background: 'transparent', color: acc, border: `1px solid ${acc}`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              🔄 Sync Masters
            </button>
            <label style={{ padding: '10px 16px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'inline-block' }}>
              📥 Import
              <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const r = await bulkAPI.importProducts(f); alert(`Import done! ${r.data.imported} imported, ${r.data.skipped} skipped.${r.data.errors?.length ? '\nErrors: ' + r.data.errors.join('\n') : ''}`); window.location.reload(); } catch (err) { alert('Import failed: ' + (err.response?.data?.detail || err.message)); } e.target.value = ''; }} />
            </label>
            <button onClick={async () => { try { const r = await bulkAPI.productTemplate(); const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'products_template.xlsx'; a.click(); URL.revokeObjectURL(url); } catch { alert('Download failed'); } }}
              style={{ padding: '10px 12px', background: 'transparent', color: acc, border: 'none', borderRadius: 9, fontWeight: 600, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
              📋 Template
            </button>
          </div>
        </div>

        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 20 }}>
            {[
              ['TOTAL PRODUCTS', stats.total_products, '🛒', darkMode ? '#F1F5F9' : '#0F172A'],
              ['LOW STOCK', stats.low_stock, '🔻', '#d97706'],
              ['OUT OF STOCK', stats.out_of_stock, '🚫', '#dc2626'],
              ['INVENTORY VALUE', `₹${(stats.total_inventory_value || 0).toLocaleString('en-IN')}`, '💎', '#16a34a'],
              ['POTENTIAL SELL VALUE', `₹${(stats.total_potential_sell_value || 0).toLocaleString('en-IN')}`, '📈', '#2563eb'],
            ].map(([label, val, icon, clr]) => (
              <div key={label} style={{ ...card, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ fontSize: 24, padding: 10, background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 10 }}>{icon}</span>
                <div><div style={{ fontSize: 20, fontWeight: 800, color: clr }}>{val}</div><div style={{ ...muted, fontSize: 12, marginTop: 2 }}>{label}</div></div>
              </div>
            ))}
          </div>
        )}

        <div style={{ ...card, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <input ref={searchRef} style={{ ...inp, flex: 1, minWidth: 200 }} placeholder="Search by name or SKU…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <select style={inp} value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setSubcategoryFilter(''); setPage(1); }}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select style={inp} value={subcategoryFilter} onChange={e => { setSubcategoryFilter(e.target.value); setPage(1); }}>
            <option value="">All Subcategories</option>
            {subcategories.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select style={inp} value={brandFilter} onChange={e => { setBrandFilter(e.target.value); setPage(1); }}>
            <option value="">All Brands</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select style={inp} value={supplierFilter} onChange={e => { setSupplierFilter(e.target.value); setPage(1); }}>
            <option value="">All Suppliers</option>
            {suppliers.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}{s.product_count > 0 && s.id !== '__orphan__' ? ` (${s.product_count})` : ''}
              </option>
            ))}
          </select>
          <select style={inp} value={stockStatusFilter} onChange={e => { setStockStatusFilter(e.target.value); setPage(1); }}>
            <option value="all">All Stock</option>
            <option value="available">Available Stock</option>
            <option value="negative">Negative Stock</option>
          </select>
        </div>

        <div className="stagger-list" style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, ...muted }}>Loading products…</div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 56, marginBottom: 12 }}>📦</div>
              <p style={{ ...muted, marginBottom: 16 }}>No products found</p>
              <button onClick={() => setShowModal(true)} style={{ padding: '10px 24px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Add First Product</button>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                  <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                    {['Product', 'SKU', 'Brand', 'Category', 'Purchase', 'Selling', 'MRP', 'GST', 'HSN', 'Stock', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, i) => {
                    const isLow = p.current_stock > 0 && p.current_stock <= p.low_stock_threshold;
                    const isOut = p.current_stock === 0;
                    return (
                      <tr key={p.id} onClick={() => setSelectedIndex(i)} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, background: selectedIndex === i ? (darkMode ? '#334155' : '#EEF2FF') : undefined, cursor: 'pointer', transition: 'background 0.15s' }}>
                        <td style={{ padding: '11px 14px' }}>
                          <div style={{ fontWeight: 700, textTransform: 'uppercase', ...pg }}>{p.name}</div>
                          {p.barcode && <div style={{ fontSize: 10, fontFamily: 'monospace', textTransform: 'uppercase', ...muted }}>🔖 {p.barcode}</div>}
                        </td>
                        <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 12, textTransform: 'uppercase', ...muted }}>{p.sku}</td>
                        <td style={{ padding: '11px 14px', fontSize: 12, textTransform: 'uppercase', ...muted }}>{p.brand || '—'}</td>
                        <td style={{ padding: '11px 14px', fontSize: 12, textTransform: 'uppercase' }}>
                          {p.category ? <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: darkMode ? '#334155' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#374151' }}>{p.category}</span> : <span style={muted}>—</span>}
                        </td>
                        <td style={{ padding: '11px 14px', fontSize: 13, ...muted }}>₹{p.purchase_price}</td>
                        <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 700, color: '#16a34a' }}>₹{p.selling_price}</td>
                        <td style={{ padding: '11px 14px', fontSize: 12, ...muted }}>{p.mrp ? `₹${p.mrp}` : '—'}</td>
                        <td style={{ padding: '11px 14px', fontSize: 13, ...muted }}>{p.gst_percent}%</td>
                        <td style={{ padding: '11px 14px', fontSize: 11, fontFamily: 'monospace', textTransform: 'uppercase', ...muted }}>{p.hsn_sac || '—'}</td>
                        <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 700, color: isOut ? '#dc2626' : isLow ? '#d97706' : darkMode ? '#F1F5F9' : '#0F172A' }}>
                          {isOut ? '🚫 0' : isLow ? `🔻 ${p.current_stock}` : p.current_stock}
                        </td>
                        <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                          <button onClick={() => { setSelected(p); setShowRegisterModal(true); }} style={{ marginRight: 8, fontSize: 12, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>📋 Register</button>
                          <button onClick={() => { setSelected(p); setShowStockModal(true); }} style={{ marginRight: 8, fontSize: 12, color: '#0ea5e9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>📊 Stock</button>
                          <button onClick={() => { openEditProduct(p.id); }} style={{ marginRight: 8, fontSize: 12, color: acc, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>✏️ Edit</button>
                          <button onClick={() => handleDelete(p.id)} style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>🗑</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              {totalPages > 1 && (
                <div style={{ padding: '12px 16px', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                    style={{ padding: '6px 16px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, background: 'transparent', ...pg, cursor: 'pointer' }}>◀ Prev</button>
                  <span style={{ fontSize: 13, ...muted }}>Page {page} of {totalPages} — {total} products</span>
                  <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
                    style={{ padding: '6px 16px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, background: 'transparent', ...pg, cursor: 'pointer' }}>Next ▶</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && (
        <ProductModal product={selected}
          onClose={() => {
            setShowModal(false); setSelected(null);
            if (openedFromUrl.current) { openedFromUrl.current = false; const dest = sourceRoute.current || '/dashboard'; sourceRoute.current = null; navigate(dest, { replace: true }); }
          }}
          onSuccess={() => { const fromUrl = openedFromUrl.current; const dest = sourceRoute.current; openedFromUrl.current = false; sourceRoute.current = null; setShowModal(false); setSelected(null); setPage(1); refreshProducts(true); productAPI.stats().then(r => setStats(r.data)).catch(() => {}); if (fromUrl) navigate(dest || '/dashboard', { replace: true }); }} />
      )}

      {showRegisterModal && selected && (
        <ItemRegisterModal product={selected}
          onClose={() => { setShowRegisterModal(false); setSelected(null); }} />
      )}
      {showStockModal && selected && (

        <StockModal product={selected}
          onClose={() => { setShowStockModal(false); setSelected(null); }}
          onSuccess={() => { setShowStockModal(false); setSelected(null); setPage(1); refreshProducts(true); productAPI.stats().then(r => setStats(r.data)).catch(() => {}); }} />
      )}
    </Layout>
  );
};




const ItemRegisterModal = ({ product, onClose }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    setLoading(true);
    productAPI.getRegister(product.id)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load register'))
      .finally(() => setLoading(false));
  }, [product.id]);

  const typeColor = (type) => {
    switch(type) {
      case 'Purchase': return '#16a34a';
      case 'Sale': return '#dc2626';
      case 'Sale Return': return '#0ea5e9';
      case 'Purchase Return': return '#d97706';
      default: return pg.color;
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 10, overflowY: 'auto' }}>
      <div className="animate-scaleIn" style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: 18, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: 12, marginTop: 4 }}>
        <div style={{ background: cardBg, borderRadius: 14, border: `2px solid ${border}` }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ ...pg, fontSize: 18, fontWeight: 800, margin: 0 }}>📋 Item Register</h3>
              <div style={{ ...muted, fontSize: 13, marginTop: 2 }}>{product.name} &middot; SKU: {product.sku}</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8' }}>&times;</button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, ...muted }}>Loading register...</div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#dc2626' }}>{error}</div>
          ) : data && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, padding: '14px 20px' }}>
                {[
                  ['Opening Stock', data.opening_stock, '#6366f1'],
                  ['Total IN', data.total_in, '#16a34a'],
                  ['Total OUT', data.total_out, '#dc2626'],
                  ['Closing Stock', data.closing_stock, '#0ea5e9'],
                  ['Current Stock', data.current_stock, darkMode ? '#F1F5F9' : '#0F172A'],
                ].map(([label, val, clr]) => (
                  <div key={label} style={{ padding: '10px 14px', background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 10, border: `1px solid ${border}` }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: clr }}>{val}</div>
                    <div style={{ ...muted, fontSize: 11, marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>

              <div style={{ overflowX: 'auto', margin: '0 0 12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                  <thead>
                    <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${border}` }}>
                      {['#', 'Date', 'Type', 'Document No', 'Party', 'Qty IN', 'Qty OUT', 'Balance'].map(h => (
                        <th key={h} style={{ padding: '9px 12px', textAlign: h === 'Qty IN' || h === 'Qty OUT' || h === 'Balance' || h === '#' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: `1px solid ${border}`, background: darkMode ? '#1a1a3e' : '#EEF2FF' }}>
                      <td style={{ padding: '8px 12px', textAlign: 'right', ...muted, fontSize: 12 }}>&mdash;</td>
                      <td style={{ padding: '8px 12px', fontWeight: 700, ...pg, fontSize: 13 }}>Opening</td>
                      <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#6366f1' }}>Opening Balance</td>
                      <td style={{ padding: '8px 12px', ...muted, fontSize: 12 }}>&mdash;</td>
                      <td style={{ padding: '8px 12px', ...muted, fontSize: 12 }}>&mdash;</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#16a34a', fontSize: 13 }}>{data.opening_stock}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', ...muted, fontSize: 13 }}>&mdash;</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, ...pg, fontSize: 13 }}>{data.opening_stock}</td>
                    </tr>
                    {data.movements.map((m, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ padding: '8px 12px', textAlign: 'right', ...muted, fontSize: 12 }}>{i + 1}</td>
                        <td style={{ padding: '8px 12px', fontSize: 13, ...pg }}>{m.date || '—'}</td>
                        <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: typeColor(m.type) }}>{m.type}</td>
                        <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace', color: acc }}>{m.doc_no}</td>
                        <td style={{ padding: '8px 12px', fontSize: 12, ...muted, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.party_name || '—'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: m.qty_in > 0 ? '#16a34a' : undefined, fontSize: 13 }}>{m.qty_in > 0 ? m.qty_in : '—'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: m.qty_out > 0 ? '#dc2626' : undefined, fontSize: 13 }}>{m.qty_out > 0 ? m.qty_out : '—'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, fontSize: 13, color: m.balance < 0 ? '#dc2626' : pg.color }}>{m.balance}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: `2px solid ${border}`, background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                      <td colSpan={5} style={{ padding: '10px 12px', fontWeight: 800, ...pg, fontSize: 13, textAlign: 'right' }}>TOTALS</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, color: '#16a34a', fontSize: 14 }}>{data.total_in}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, color: '#dc2626', fontSize: 14 }}>{data.total_out}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, fontSize: 14, color: data.closing_stock < 0 ? '#dc2626' : pg.color }}>{data.closing_stock}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {data.movements.length === 0 && (
                <div style={{ textAlign: 'center', padding: '24px 0 32px', ...muted }}>No movements recorded for this product.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};



const StockModal = ({ product, onClose, onSuccess }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const [adjustment, setAdjustment] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const parsed = parseInt(adjustment) || 0;
  const validatedAdjustment = Math.max(-product.current_stock, parsed);
  const newStock = Math.max(0, product.current_stock + validatedAdjustment);

  const handleClose = useCallback(() => {
    if ((adjustment || notes) && !window.confirm('Discard this stock adjustment? All unsaved changes will be lost.')) return;
    onClose();
  }, [adjustment, notes, onClose]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [handleClose]);

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true);
    try { await productAPI.adjustStock(product.id, validatedAdjustment, notes); onSuccess(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed to adjust stock'); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div className="animate-scaleIn" style={{ background: cardBg, borderRadius: 16, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '18px 24px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between' }}>
            <h3 style={{ color: darkMode ? '#F1F5F9' : '#0F172A', fontSize: 18, fontWeight: 800, margin: 0 }}>{String.fromCodePoint(0x1F4CA)} Adjust Stock</h3>
            <button type="button" onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8' }}>×</button>
          </div>
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ padding: '12px 16px', background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8 }}>
              <span style={{ color: darkMode ? '#94A3B8' : '#64748B', fontSize: 13 }}>{product.name} · Current stock: </span>
              <strong style={{ color: darkMode ? '#F1F5F9' : '#0F172A', fontSize: 15 }}>{product.current_stock}</strong>
            </div>
            <div>
              <label style={{ color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Adjustment (+ add, − remove) *</label>
              <input required style={inp} type="number" value={adjustment} onChange={e => setAdjustment(e.target.value)} placeholder="e.g. 50 or -10" />
              {adjustment && <div style={{ marginTop: 6, fontSize: 13, color: '#3b82f6' }}>New stock: <strong>{newStock}</strong></div>}
            </div>
            <div>
              <label style={{ color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Notes (optional)</label>
              <input style={inp} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Received from supplier" />
            </div>
          </div>
          <div style={{ padding: '14px 24px', borderTop: `1px solid ${border}`, display: 'flex', gap: 10 }}>
            <button type="button" onClick={handleClose} style={{ flex: 1, padding: '9px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B' }}>Cancel</button>
            <button type="submit" disabled={saving || !adjustment} style={{ flex: 1, padding: '9px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
              {saving ? 'Adjusting…' : 'Adjust Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Products;
